import { MouseEvent } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { styled } from '@mui/material'
import { DiscussionTargetType } from '../../../../../domain/entity/DiscussionEntity'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import RoundChatIcon from '../../../../components/icons/RoundChatIcon'
import { EntityIcon } from '../EntityIcon'
import ticketListIcon from '../../../../../assets/icons/global/ticket_list.svg'

type RowData = {
  uuid: string
  name: string
  iconUrl?: string
}

export type ProgressReportNameCellRendererParams =
  ICellRendererParams<RowData> & {
    targetType: DiscussionTargetType
    editable: boolean
  }

export const ProgressReportNameCellRenderer = ({
  data,
  value,
  targetType,
  editable,
}: ProgressReportNameCellRendererParams) => {
  const { startTargetDiscussion } = useDiscussionActionContext()

  if (!data) return null

  const handleIconClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    startTargetDiscussion({
      uuid: data.uuid,
      name: data.name,
      type: targetType,
    })
  }

  const ariaLabelId = DISCUSSION_TRIGGER_LABEL_IDS[targetType]

  return (
    <Container>
      {targetType === 'TEAM' && <EntityIcon src={data.iconUrl} size={20} />}
      {/*
        チケット管理表別進捗状況の行は、プロジェクト計画のチケット管理表と同じ見た目にする。
        WbsItemIcon が「チケット種別のアイコン＋ticket_list.svg」を並べて出しているので、それに揃える。
        EntityIcon（MUI の Avatar）は丸く切り抜くため、ここでは使わない
      */}
      {targetType === 'TICKET_LIST' && data.iconUrl && (
        <TicketListIcon>
          <TypeIcon src={data.iconUrl} alt="" />
          <ListBadgeIcon src={ticketListIcon} alt="" />
        </TicketListIcon>
      )}
      <NameText>{value}</NameText>
      {editable && (
        <TriggerButton
          type="button"
          onClick={handleIconClick}
          aria-label={intl.formatMessage({ id: ariaLabelId })}
        >
          <StyledChatIcon />
        </TriggerButton>
      )}
    </Container>
  )
}

const TicketListIcon = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
})

const TypeIcon = styled('img')({
  height: '20px',
})

// プロジェクト計画（TicketListSubMenuItemsIcon）と同じく、バッジは種別アイコンより小さくする
const ListBadgeIcon = styled('img')({
  height: '16px',
})

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minWidth: 0,
  overflow: 'hidden',
  gap: '6px',
  paddingLeft: '4px',
})

const TriggerButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '26px',
  height: '26px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const StyledChatIcon = styled(RoundChatIcon)({
  height: '18px',
  color: colorPalette.monotone[4],
  fill: 'none',
})

const NameText = styled('div')({
  flex: 1,
  minWidth: 0,
  maxHeight: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const DISCUSSION_TRIGGER_LABEL_IDS: Record<DiscussionTargetType, string> = {
  PROCESS: 'discussion.trigger.process',
  TEAM: 'discussion.trigger.team',
  TICKET_LIST: 'discussion.trigger.ticketList',
}
